﻿import { logger } from '@fe/core';
import { Window } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { AirportDiversionManagementInfo } from '../../airports/airport';
import { useEvent } from '../../app/contexts/event-bus';
import { useAppSelector } from '../../app/hooks/store-hooks';
import { useFlightWorker } from '../../app/hooks/use-flight-worker';
import { diversionSettingsSelector } from '../../app/state/selectors';
import { DateTimeFormatter } from '../../formatters';
import { DiversionManagementInfo, MessageType } from '../../services/actor-messages';
import {
    formatCapacityValueByType,
    formatStatus,
    formatTime,
    formatTimeOnboardAircraft,
    formatTotalCapacity,
    getCapacityHeaderText
} from './diversion-formatters';

const DiversionManager = () => {
    const [response, setResponse] = React.useState<DiversionManagementInfo | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
    const settings = useAppSelector(diversionSettingsSelector);
    const [diversionState, setDiversionState] = React.useState<{ airportId: string | null }>({ airportId: null });
    const location = useLocation();
    const worker = useFlightWorker();

    useEvent(
        'show-diversion',
        React.useCallback((airportId: string) => {
            setDiversionState({ airportId: airportId });
        }, [])
    );

    useEffect(() => {
        setDiversionState({ airportId: null });
    }, [location]);

    React.useEffect(() => {
        if (!diversionState.airportId) {
            return;
        }

        setLoading(true);
        setError(null);
        setResponse(null);

        const load = async () => {
            if (!diversionState.airportId) {
                setLoading(false);
                setShowSpinner(false);
                return;
            }
            try {
                const response = await worker.actor.sendAsync({
                    type: MessageType.getDiversionInfo,
                    data: diversionState.airportId
                });

                if (response) {
                    response.diversionInfo.planes.items = [...response.diversionInfo.planes.items].sort((a, b) => {
                        const timeA = a.groundTime || 0;
                        const timeB = b.groundTime || 0;
                        return timeB - timeA;
                    });

                    if (response.holdingInfo && response.holdingInfo.items) {
                        response.holdingInfo.items = [...response.holdingInfo.items].sort((a, b) => {
                            const timeA = a.enterTime || 0;
                            const timeB = b.enterTime || 0;
                            return timeB - timeA;
                        });
                    }

                    setResponse(response);
                    setError(null);
                } else {
                    setError(`Failed to load diversion information for ${diversionState.airportId}`);
                }
            } catch (err) {
                logger.error(err);
                setError(`Error loading diversion information: ${err instanceof Error ? err.message : 'Unknown error'}`);
            } finally {
                setLoading(false);
                setShowSpinner(false);
            }
        };
        load();
    }, [diversionState]);

    const handleClose = () => {
        setDiversionState({ airportId: null });
    };

    const getClosureText = (diversionInfo: AirportDiversionManagementInfo) => {
        let text = '';
        switch (diversionInfo.closure) {
            case 0:
            default:
                return 'None';
            case 1:
                text = 'Airline';
                break;
            case 2:
                text = 'Airport';
                break;
        }
        if (diversionInfo.closeStart) {
            text += ` Start: ${DateTimeFormatter.formatDateTime(diversionInfo.closeStart)}`;
        }
        if (diversionInfo.closeEnd) {
            text += ` End: ${DateTimeFormatter.formatDateTime(diversionInfo.closeEnd)}`;
        }
        return text;
    };

    const formatPhoneNumber = (phone: string | null | undefined): string => {
        if (!phone) {
            return 'N/A';
        }

        const digits = phone.replace(/\D/g, '');

        if (digits.length === 10) {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
        } else if (digits.length === 11 && digits.startsWith('1')) {
            return digits.replace(/1(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3');
        }

        return phone;
    };

    //todo adjust based on settings (numerical, faa (current))
    const formatPriority = (priority: number): string => {
        switch (priority) {
            case 0:
                return 'None';
            case 8:
                return 'Yes';
            case 15:
                return 'High';
            default:
                return 'Invalid';
        }
    };

    const formatExitedHold = (exitTime: number | null, exitType: number): string => {
        if (exitTime === null) {
            return '';
        }
        const timeStr = DateTimeFormatter.formatTime(exitTime);
        const typeStr = exitType === 0 ? '' : '(Est.)'; // Actual or Estimate
        return `${timeStr} ${typeStr}`;
    };

    const formatHoldTime = (enterTime: number | null): string => {
        if (enterTime === null) {
            return '';
        }
        return DateTimeFormatter.formatTime(enterTime);
    };

    const formatCurrentHoldTime = (enterTime: number | null, exitTime: number | null, exitType: number): string => {
        if (enterTime === null) {
            return '';
        }

        let endTime: dayjs.Dayjs;
        if (exitType !== 0 && exitTime !== null) {
            endTime = dayjs.unix(exitTime);
        } else {
            endTime = dayjs.utc();
        }

        const startTime = dayjs.unix(enterTime);
        const diffMinutes = endTime.diff(startTime, 'minutes');
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const formatCumulativeHoldTime = (
        enterTime: number | null,
        exitTime: number | null,
        exitType: number,
        totalHoldMinutes: number
    ): string => {
        let cHoldTime: number;

        if (enterTime !== null) {
            let endTime: dayjs.Dayjs;
            if (exitType !== 0 && exitTime !== null) {
                endTime = dayjs.unix(exitTime);
            } else {
                endTime = dayjs.utc();
            }

            const startTime = dayjs.unix(enterTime);
            const currentHoldMinutes = endTime.diff(startTime, 'minutes');

            if (exitType !== 0 || exitTime === null) {
                cHoldTime = currentHoldMinutes + totalHoldMinutes;
            } else {
                cHoldTime = totalHoldMinutes;
            }
        } else {
            cHoldTime = totalHoldMinutes;
        }

        const hours = Math.floor(cHoldTime / 60);
        const minutes = cHoldTime % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const formatSource = (source: number): string => {
        switch (source) {
            case 0:
                return 'FE';
            case 1:
                return 'FAA';
            default:
                return 'Unknown';
        }
    };

    const formatAltitude = (holdingItem: any): string => {
        if (response?.holdingPlanes) {
            const plane = response.holdingPlanes.find((p: any) => p.acid === holdingItem.acid);
            if (plane && plane.altitude) {
                if (plane.altitude === 0x3ff) {
                    return 'Gnd';
                } else {
                    return plane.altitude.toString();
                }
            }
        }
        return '';
    };

    const renderContent = () => {
        if (loading && showSpinner) {
            return <Loader type={'infinite-spinner'} size="medium" className="mb-2 center" />;
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <div className="text-danger mb-3">{error}</div>
                </div>
            );
        }

        if (!response) {
            return (
                <div className="text-center p-4">
                    <div>No data available</div>
                </div>
            );
        }

        return (
            <div className="diversion-manager">
                <div className="text-center mb-4">
                    <span className="me-4">
                        Total Diversions{' '}
                        {(response.diversionInfo.divert.airline.total ?? 0) + (response.diversionInfo.divert.network.total ?? 0)}/
                        {response.diversionInfo.totalDiversions} (all)
                    </span>
                    <span>Capacity {formatTotalCapacity(response.diversionInfo.capacity, settings)}</span>
                </div>

                {/* Capacity/Actual/Available Table */}
                <table className="cobalt-table cobalt-alternate w-100 mb-4">
                    <thead>
                        <tr>
                            <th></th>
                            <th>{getCapacityHeaderText('Airline', settings)}</th>
                            <th>{getCapacityHeaderText('Network', settings)}</th>
                            <th>{getCapacityHeaderText('Other', settings)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Capacity</td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.capacity.airline.narrow,
                                    response.diversionInfo.capacity.airline.wide,
                                    response.diversionInfo.capacity.airline.total,
                                    settings.capacity.airline
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.capacity.network.narrow,
                                    response.diversionInfo.capacity.network.wide,
                                    response.diversionInfo.capacity.network.total,
                                    settings.capacity.network
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.capacity.other.narrow,
                                    response.diversionInfo.capacity.other.wide,
                                    response.diversionInfo.capacity.other.total,
                                    settings.capacity.other
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Actual</td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.divert.airline.narrow,
                                    response.diversionInfo.divert.airline.wide,
                                    response.diversionInfo.divert.airline.total,
                                    settings.capacity.airline,
                                    '0'
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.divert.network.narrow,
                                    response.diversionInfo.divert.network.wide,
                                    response.diversionInfo.divert.network.total,
                                    settings.capacity.network,
                                    '0'
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.divert.other.narrow,
                                    response.diversionInfo.divert.other.wide,
                                    response.diversionInfo.divert.other.total,
                                    settings.capacity.other,
                                    '0'
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Available</td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.available.airline.narrow,
                                    response.diversionInfo.available.airline.wide,
                                    response.diversionInfo.available.airline.total,
                                    settings.capacity.airline
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.available.network.narrow,
                                    response.diversionInfo.available.network.wide,
                                    response.diversionInfo.available.network.total,
                                    settings.capacity.network
                                )}
                            </td>
                            <td>
                                {formatCapacityValueByType(
                                    response.diversionInfo.available.other.narrow,
                                    response.diversionInfo.available.other.wide,
                                    response.diversionInfo.available.other.total,
                                    settings.capacity.other
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="pb-2" style={{ display: 'flex', flexDirection: 'column', height: '400px' }}>
                    <div
                        style={{
                            flex: '0 1 auto',
                            marginBottom: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '120px',
                            maxHeight: '70%'
                        }}>
                        <div>Flights Diverting To</div>
                        <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 2
                                        }}>
                                        <th>Acid</th>
                                        <th>ACType</th>
                                        <th>Origin</th>
                                        <th>Dest(old)</th>
                                        <th>Status</th>
                                        <th>Time (+/-)</th>
                                        <th>RecPri</th>
                                        <th>TOA</th>
                                        <th>Desk</th>
                                        <th>Scratchpad</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {response.diversionInfo.planes.items.length > 0 ? (
                                        response.diversionInfo.planes.items.map((plane, index) => (
                                            <tr key={plane.recordId || index}>
                                                <td>{plane.acid}</td>
                                                <td>{plane.aircraftType || ''}</td>
                                                <td>{plane.origin}</td>
                                                <td>{plane.originalDestination}</td>
                                                <td>{formatStatus(plane.status, plane.subStatus)}</td>
                                                <td>{formatTime(plane.status === 0 ? plane.flightTime : plane.groundTime)}</td>
                                                <td>{formatPriority(plane.recoveryPriority)}</td>
                                                <td>{formatTimeOnboardAircraft(plane.blockTime)}</td>
                                                <td>{plane.sectorDeskId || ''}</td>
                                                <td>{plane.scratchPad || ''}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} className="text-center">
                                                No items.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div
                        style={{
                            flex: '1 1 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '120px'
                        }}>
                        <div>Flights Holding Into</div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 1
                                        }}>
                                        <th>Acid</th>
                                        <th>ACtype</th>
                                        <th>Origin</th>
                                        <th>Altitude</th>
                                        <th>Fix</th>
                                        <th>Entered Hold</th>
                                        <th>Cur. Hold Time</th>
                                        <th>Cum. Hold Time</th>
                                        <th>Exited Hold</th>
                                        <th>Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {response.holdingInfo && response.holdingInfo.items.length > 0 ? (
                                        response.holdingInfo.items.map((holdingItem, index) => (
                                            <tr key={holdingItem.recordID || index}>
                                                <td>{holdingItem.acid}</td>
                                                <td>{holdingItem.aircraftType || ''}</td>
                                                <td>{holdingItem.origin || ''}</td>
                                                <td>{formatAltitude(holdingItem)}</td>
                                                <td>{holdingItem.holdFix || ''}</td>
                                                <td>{formatHoldTime(holdingItem.enterTime)}</td>
                                                <td>
                                                    {formatCurrentHoldTime(
                                                        holdingItem.enterTime,
                                                        holdingItem.exitTime,
                                                        holdingItem.exitType
                                                    )}
                                                </td>
                                                <td>
                                                    {formatCumulativeHoldTime(
                                                        holdingItem.enterTime,
                                                        holdingItem.exitTime,
                                                        holdingItem.exitType,
                                                        holdingItem.totalHoldMinutes
                                                    )}
                                                </td>
                                                <td>{formatExitedHold(holdingItem.exitTime, holdingItem.exitType)}</td>
                                                <td>{formatSource(holdingItem.source)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} className="text-center">
                                                No items.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Closure, Phone, Comments */}
                <div className="mb-2">Closure: {getClosureText(response.diversionInfo)}</div>
                <div className="mb-2">Phone: {formatPhoneNumber(response.diversionInfo.phoneNumber)}</div>
                <div className="mb-2">
                    Comment:
                    <div>{response.diversionInfo.comments || 'No comments'}</div>
                </div>
            </div>
        );
    };

    if (!diversionState.airportId) {
        return <></>;
    }

    return (
        <Window
            initialWidth={1000}
            initialHeight={780}
            title={`Diversion/Holding Status${response ? ' - ' : ''}${response?.airportName ? response.airportName : ''}`}
            onClose={handleClose}>
            {renderContent()}
        </Window>
    );
};

export default DiversionManager;
